import React from 'react';
import { Box, Typography } from '@mui/material';
import { renderTooltipIcon } from '../TableauObs/common/KpiCard';
import { formatValue } from 'components/PowerBIObservability/UserActivity/helper';
import LoaderComponent from 'components/Loader';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BillingCycleItem {
    label: string;
    value: number;
    percent: number;
    isCost: boolean;
}

interface BillingCycleDistributionProps {
    data: BillingCycleItem[];
    isLoading?: boolean;
    containerProps?: {
        sx?: object;
    };
    hasTooltip?: boolean;
    tooltipText?: string;
    theme?: 'light' | 'dark';
    customBarColor?: string; 
    customBackgroundColor?: string; 
    isNumberBold?: boolean; 
    title?:string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDisplayValue = (item: BillingCycleItem): string => {
    if (item.isCost) {
        return `$${formatValue(item.value)}`;
    }
    return `${item.value} Items`;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const BarRow: React.FC<{ item: BillingCycleItem; customBarColor?: string; customBackgroundColor?: string; isNumberBold?:boolean}> = ({ 
    item, 
    customBarColor, 
    customBackgroundColor,
    isNumberBold
}) => {
    const getBarColor = () => {
        if (customBarColor) return customBarColor;
        return item.isCost ? '#26C6DA' : '#a1a1a1';
    };

    const getBackgroundColor = () => {
        if (customBackgroundColor) return customBackgroundColor;
        return item.isCost ? '#e0f7fa' : '#e8e8e8';
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography
                    sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: (item.isCost || isNumberBold) ? '#1a1a2e' : '#9e9e9e',
                    }}
                >
                    {item.label}
                </Typography>
                <Typography
                    sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: (item.isCost || isNumberBold) ? '#1a1a2e' : '#9e9e9e',
                    }}
                >
                    {isNumberBold ? `${item.value}`: formatDisplayValue(item)}
                </Typography>
            </Box>
            <Box
                sx={{
                    width: '100%',
                    height: 20,
                    backgroundColor: getBackgroundColor(),
                    borderRadius: '99px',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        width: `${item.percent}%`,
                        height: '100%',
                        backgroundColor: getBarColor(),
                        borderRadius: '99px',
                        transition: 'width 0.6s ease-in-out',
                    }}
                />
            </Box>
        </Box>
    );
};

const renderContent = (data: BillingCycleItem[], customBarColor?: string, customBackgroundColor?: string, isNumberBold?:boolean) => {
    if (data.length === 0) {
        return (
            <Typography sx={{ textAlign: 'center', color: '#9e9e9e', fontSize: 14 }}>
                No Data available.
            </Typography>
        );
    }
    return data.map((item) => (
        <BarRow 
            key={item.label} 
            item={item} 
            customBarColor={customBarColor}
            customBackgroundColor={customBackgroundColor}
            isNumberBold={isNumberBold}
        />
    ));
};

// ─── Main Component ───────────────────────────────────────────────────────────

const BillingCycleDistribution: React.FC<BillingCycleDistributionProps> = ({
    data,
    isLoading = false,
    hasTooltip = false,
    tooltipText,
    containerProps,
    theme = 'light',
    customBarColor,
    customBackgroundColor,
    isNumberBold=false,
    title = "Billing Cycle Distribution"
}) => (
    <Box
        sx={{
            minHeight: '318px',
            backgroundColor: 'rgb(255, 255, 255)',
            borderRadius: '5px',
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
            ...containerProps?.sx,
        }}
    >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography
                variant="widgetTitle"
                sx={{ color: '#1a1a2e', fontSize: 20, fontWeight: 500 }}
            >
                {title}
            </Typography>
            {hasTooltip && renderTooltipIcon(tooltipText || "", theme)}
        </Box>

        {/* Bar Rows */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, justifyContent: 'center' }}>
            {isLoading || !data
                ? <LoaderComponent pt={8} />
                : renderContent(data, customBarColor, customBackgroundColor,isNumberBold)
            }
        </Box>
    </Box>
);

export default BillingCycleDistribution;